import { Message } from "@shared/schema";
import { sanitizeHtml } from '@/lib/sanitize-html';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, FileText, Image } from "lucide-react";
import { format } from "date-fns";

interface MessageModalProps {
  message: Message;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function MessageModal({ message, open, onOpenChange }: MessageModalProps) {
  const getFileIcon = (type: string) => {
    if (type.includes('pdf')) return <FileText className="w-4 h-4 text-red-400" />;
    if (type.includes('image')) return <Image className="w-4 h-4 text-green-400" />;
    return <FileText className="w-4 h-4 text-gray-400" />;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden navy-card border-gray-600">
        <DialogHeader className="border-b border-gray-600 pb-4">
          <DialogTitle className="text-lg font-semibold text-white break-all">
            {message.messageType === 'inbound' ? 'Email from Client' : 
             message.messageType === 'outbound' ? 'Email to Client' : 
             'Direct Message'}
          </DialogTitle>
        </DialogHeader>

        <div className="overflow-y-auto max-h-[calc(90vh-120px)] space-y-4">
          {/* Email Header */}
          <div className="navy-bg rounded-lg p-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              {message.fromEmail && (
                <div>
                  <span className="text-gray-400">From:</span>
                  <span className="text-white ml-2">{message.fromEmail}</span>
                </div>
              )}
              {message.toEmail && (
                <div>
                  <span className="text-gray-400">To:</span>
                  <span className="text-white ml-2">{message.toEmail}</span>
                </div>
              )}
              <div>
                <span className="text-gray-400">Date:</span>
                <span className="text-white ml-2">
                  {format(new Date(message.dateCreated), 'MMMM dd, yyyy h:mm a')}
                </span>
              </div>
              {message.subject && (
                <div>
                  <span className="text-gray-400">Subject:</span>
                  <span className="text-white ml-2">{message.subject}</span>
                </div>
              )}
            </div>
          </div>

          {/* Message Content */}
          <div className="navy-bg rounded-lg p-4">
            <div 
              className="text-gray-200 leading-relaxed whitespace-pre-wrap"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(message.content) }}
            />
          </div>

          {/* Attachments */}
          {message.attachments && message.attachments.length > 0 && (
            <div className="navy-bg rounded-lg p-4">
              <h4 className="text-sm font-medium text-white mb-3">Attachments</h4>
              <div className="space-y-2">
                {message.attachments.map((attachment, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between navy-card rounded-lg p-3 border border-gray-600"
                  >
                    <div className="flex items-center">
                      {getFileIcon(attachment.type)}
                      <div className="ml-3">
                        <div className="text-sm text-white">{attachment.name}</div>
                        <div className="text-xs text-gray-400">
                          {(attachment.size / 1024 / 1024).toFixed(1)} MB
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-blue-400 hover:text-blue-300"
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
